import React, { useState, useEffect, useRef } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Container, CircularProgress } from '@mui/material';

import LanguageDashboard from './LanguageDashboard';

import { useParams, useNavigate } from 'react-router-dom';
import languageLearningApi from '../middleware/languageLearningApi';
import { useCourse } from '../../../context/CourseContext';
import { useNavigation } from '../../../context/NavigationContext';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2', // Primary blue
      light: '#4791db',
      dark: '#115293'
    },
    background: {
      default: '#f4f6f9', // Light blue-gray background
      paper: '#ffffff'
    },
    text: {
      primary: '#0d47a1' // Dark blue text
    }
  },
  typography: {
    fontFamily: 'Roboto, Arial, sans-serif'
  }
});

const LanguageLearningApp = ({ token, setToken }) => {
  const navigate = useNavigate();
  const { courseId } = useParams();
  
  // Use both contexts
  const { updateCourse: updateCourseContext } = useCourse();
  const { updateCourse: updateNavigationCourse } = useNavigation();

  const [course, setCourse] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Use a ref to track mounting state
  const isMounted = useRef(true);
  
  // Store previous courseId to prevent unnecessary effects
  const prevCourseIdRef = useRef(courseId);

  // Only run effect on initial mount and when courseId changes
  useEffect(() => {
    console.log('Effect running with courseId:', courseId);
    
    // Skip if courseId hasn't changed
    if (prevCourseIdRef.current === courseId && course !== null) {
      console.log('Skipping effect - courseId unchanged and course exists');
      return;
    }
    
    // Prevent infinite loops - only fetch if we have a valid courseId
    if (!courseId) {
      console.log('Skipping effect - no courseId provided');
      setIsLoading(false);
      return;
    }
    
    prevCourseIdRef.current = courseId;
    setIsLoading(true);
    setError(null);

    console.log('Fetching course data for courseId:', courseId);
    
    // Fetch course data
    languageLearningApi.get(`/courses/${courseId}`)
      .then((res) => {
        if (isMounted.current) {
          console.log('Course details fetched successfully');
          const courseData = res.data.course;
          
          // First update local state
          setCourse(courseData);
          setIsLoading(false);
          
          // Then update contexts if we have valid data
          if (courseData) {
            console.log('Updating contexts with course data');
            // Update both contexts with the course data
            updateCourseContext(courseData);
            updateNavigationCourse(courseData);
          }
        }
      })
      .catch(err => {
        console.error('Error fetching course:', err);
        if (isMounted.current) {
          setError('Failed to load course data. Please try again.');
          setIsLoading(false);
        }
      });

    // Set up the cleanup function for component unmount
    return () => {
      console.log('Cleanup function executed');
      isMounted.current = false;
    };
    
    // IMPORTANT: Remove the context update functions from dependencies
    // We're using useCallback in the context providers so these should be stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  // We no longer need to clear the course context on unmount
  // as the NavigationContext handles persistence across components

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <div>{error}</div>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Container maxWidth="xl" sx={{ py: 2, flexGrow: 1 }}>
            {course && <LanguageDashboard course={course} token={token} />}
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default LanguageLearningApp;